// src/components/crm/QuotePipelineStagePicker.js
// SEAM 4 VERIFIER: qpipe-4d1a7c35-picker
//
// Internal "Pipeline stage" strip on the operator quote detail page. Shows the
// stage the estimate is in and its derived outcome, and lets a person move it
// to one of the org's CUSTOM stages — or back to the system stage its status
// maps to. System stages (Sent, Won, …) otherwise follow status on their own
// (onQuotePipelineSync), so they aren't offered.
//
// INTERNAL ONLY: never mount this in a portal view, and it sits OUTSIDE the
// document card (which mirrors the customer PDF).
//
// Moves go through the setQuotePipelineStage Cloud Function — firestore.rules
// denies client writes to pipelineStageId. The outcome is never written by
// anyone; it is the stage's type (resolveStageOutcome). The page's live quote
// listener delivers the new stage after a move.
import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { ListOrdered, Lock } from 'lucide-react';
import { functions } from '../../firebase/config';
import { toJsDate } from '../../utils/formatters';
import { can, PERMISSIONS } from '../../utils/permissions';
import {
    findPipelineStage,
    resolveStageOutcome,
    deriveSystemStageId,
} from '../../utils/quoteSchema';

const OUTCOME_CHIP = {
    open:     'bg-blue-50 text-blue-700 border-blue-200',
    won:      'bg-green-50 text-green-700 border-green-200',
    lost:     'bg-red-50 text-red-700 border-red-200',
    deferred: 'bg-amber-50 text-amber-700 border-amber-200',
};
const OUTCOME_LABEL = { open: 'Open', won: 'Won', lost: 'Lost', deferred: 'Deferred' };

// qclose-3e8a5c17 — onClosed(closeActivityId, outcome) fires when the server
// says this move CLOSED the estimate (Seam 9b opens the close question). The
// server decides, from the stage types — not this component.
const QuotePipelineStagePicker = ({ orgId, quote, stages, currentUserProfile, onClosed }) => {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    if (!orgId || !quote?.id || !stages) return null;

    const currentId = quote.pipelineStageId || null;
    const current = findPipelineStage(currentId, stages);
    const outcome = resolveStageOutcome(currentId, stages);
    const since = toJsDate(quote.pipelineStageSetAt);
    // Same rule the server enforces (quotePipeline.allowedManualTargets).
    const statusStageId = deriveSystemStageId(quote, stages).target;
    const options = stages.filter((s) => !s.isSystem || s.id === statusStageId || s.id === currentId);
    const canMove = can(currentUserProfile, PERMISSIONS.MANAGE_FINANCIALS);

    const handleChange = async (e) => {
        const stageId = e.target.value;
        if (!stageId || stageId === currentId) return;
        setSaving(true);
        setError('');
        try {
            const res = await httpsCallable(functions, 'setQuotePipelineStage')({ quoteId: quote.id, stageId });
            if (res?.data?.close === 'close' && typeof onClosed === 'function') {
                onClosed(res.data.closeActivityId || null, res.data.outcome || null);
            }
        } catch (err) {
            console.error('QuotePipelineStagePicker: move failed', err);
            setError(err?.message || 'Could not move this estimate. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto mb-4 px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <ListOrdered size={16} /> Pipeline stage
                </span>
                {canMove ? (
                    <select
                        value={current ? currentId : ''}
                        onChange={handleChange}
                        disabled={saving}
                        className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white disabled:opacity-60"
                    >
                        {!current && <option value="">{currentId ? 'A removed stage' : 'Not placed yet'}</option>}
                        {options.map((s) => (
                            <option key={s.id} value={s.id}>{s.label}{s.isSystem ? ' (follows status)' : ''}</option>
                        ))}
                    </select>
                ) : (
                    <span className="flex items-center gap-1 text-sm text-gray-800">
                        {current?.isSystem && <Lock size={12} className="text-gray-400" />}
                        {current ? current.label : (currentId ? 'A removed stage' : 'Not placed yet')}
                    </span>
                )}
                {outcome && (
                    <span className={`px-2 py-0.5 text-xs border rounded-full ${OUTCOME_CHIP[outcome]}`}>{OUTCOME_LABEL[outcome]}</span>
                )}
                {since && (
                    <span className="text-xs text-gray-400">since {since.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                )}
                <span className="ml-auto text-xs text-gray-400">Internal: customers never see this</span>
            </div>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>
    );
};

export default QuotePipelineStagePicker;
